import { formatPhoneNumber } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { PhoneInputFormik } from '@strata/react-inputs';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { sendFax } from 'api/clientAPI';
import { updateFax } from 'api/referenceAPI';
import { useShowEReferralStatus } from 'services/eReferralHelpers';
import { handleError } from 'services/Error';

const Title = 'Send Fax';

const getReasonOptions = (showEReferralStatus) => [
    ...(showEReferralStatus ? ["Didn't Receive E-Referral"] : []),
    "Didn't Receive Last Fax",
    'Incorrect Fax Number',
    'Last Fax Failed',
    'Prescriber Updated',
    'Supplier Updated',
];

const faxTypes = {
    PRESCRIBER: 'Prescriber',
    SUPPLIER: 'Supplier',
};

const mapFaxTypeChange = {
    [faxTypes.PRESCRIBER]: {
        faxOverrideLabel: 'Permanently Override Prescriber Fax Number',
        faxOverrideHelperText:
            'This will override the fax number for this prescriber in ALL APPLICATIONS.',
        faxOverrideValueKey: 'prescriberFaxNumber',
        nameKey: 'prescriberName',
    },
    [faxTypes.SUPPLIER]: {
        faxOverrideLabel: 'Permanently Override Supplier Fax Number',
        faxOverrideHelperText:
            'This will override the fax number for this supplier in ALL APPLICATIONS.',
        faxOverrideValueKey: 'supplierFaxNumber',
        nameKey: 'supplierName',
    },
};

export const SendFaxDialog = ({
    isOpen,
    onClose,
    referralDetails = undefined,
    performanceEntries = undefined,
    prescriberFaxPref = undefined,
    prescriberDetails = undefined,
    supplierDetails = undefined,
}) => {
    const { prescriberFax, prescriberName, prescriberNpi } = prescriberDetails ?? {};
    const { organizationName: supplierName, fax: supplierFax, supplierNpi } = supplierDetails ?? {};

    const mapfaxTypeValues = {
        [faxTypes.PRESCRIBER]: {
            name: prescriberName,
            faxNumber: prescriberFax,
        },
        [faxTypes.SUPPLIER]: {
            name: supplierName,
            faxNumber: supplierFax,
        },
    };

    const faxTypesList =
        prescriberFaxPref?.option_value === 'ON' ? Object.values(faxTypes) : [faxTypes.SUPPLIER];

    const showEReferralStatus = useShowEReferralStatus(referralDetails, supplierDetails);

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                reason: Yup.string().required(validationStrings.requiredField.errorMessage),
                faxOverride: Yup.string().when('faxType', {
                    is: (faxType) => !mapfaxTypeValues?.[faxType]?.faxNumber,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                ...ReasonInput.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    if (values.faxOverride) {
                        await updateFax({
                            prescriberNpi,
                            supplierNpi,
                            faxNumber: values.faxOverride,
                            faxType: values.faxType.toLowerCase(),
                        });
                    }
                    await sendFax({
                        referralDetails,
                        performanceEntries,
                        faxType: values.faxType.toLowerCase(),
                        ...ReasonInput.submitValues(values),
                    });
                    SuccessToast({
                        message:
                            values.faxType.toLowerCase() === 'prescriber'
                                ? 'Prescriber Fax Sent.'
                                : 'Supplier Fax Sent.',
                    });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to send fax. Please try again.', err);
                }
            }}
        >
            {(formik) => {
                const faxTypeChange = mapFaxTypeChange?.[formik.values.faxType];
                const faxTypeValues = mapfaxTypeValues?.[formik.values.faxType];
                return (
                    <DialogWithForm
                        dialogTitle={Title}
                        width={560}
                        isOpen={isOpen}
                        secondaryButtonLabel="Cancel"
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.resetForm();
                        }}
                        primaryButtonLabel="Send Fax"
                        isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                        dialogContent={
                            <>
                                <div className="margin__bottom--medium">
                                    This action will send a fax with authorization details for this
                                    referral.
                                </div>
                                <RadioButtonGroupFormik
                                    label="Send Fax To"
                                    required
                                    options={faxTypesList}
                                    name="faxType"
                                />
                                {formik.values.faxType && (
                                    <>
                                        <div
                                            className="margin__bottom--medium"
                                            style={{ display: 'flex' }}
                                        >
                                            <DetailLabelValuePair
                                                className="margin__right--x-large"
                                                label="Name"
                                                value={faxTypeValues?.name}
                                            />
                                            <DetailLabelValuePair
                                                id="existingNumber"
                                                name="existingNumber"
                                                label="Fax Number"
                                                value={formatPhoneNumber(faxTypeValues?.faxNumber)}
                                            />
                                        </div>
                                        <PhoneInputFormik
                                            id="faxOverride"
                                            name="faxOverride"
                                            required={!faxTypeValues?.faxNumber}
                                            label={faxTypeChange?.faxOverrideLabel}
                                            helperText={faxTypeChange?.faxOverrideHelperText}
                                            disabled={formik.values.faxType === faxTypes.SUPPLIER}
                                        />
                                        <ReasonInput
                                            reasons={getReasonOptions(showEReferralStatus)}
                                        />
                                    </>
                                )}
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};

SendFaxDialog.Title = Title;

SendFaxDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
        prescriberNpi: PropTypes.string.isRequired,
        supplierNpi: PropTypes.string.isRequired,
    }),
    prescriberDetails: PropTypes.shape({
        prescriberFax: PropTypes.string.isRequired,
        prescriberName: PropTypes.string,
    }),
    supplierDetails: PropTypes.shape({
        organizationName: PropTypes.string,
        fax: PropTypes.string.isRequired,
        supplierNpi: PropTypes.string,
    }),
    prescriberFaxPref: PropTypes.shape({
        option_value: PropTypes.string.isRequired,
    }),
    performanceEntries: PropTypes.arrayOf(PropTypes.shape({})),
};
